import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "../styles/theme.css";
import "./MyAccount.css";
import Footer from "../components/Footer";
import { auth, db } from "../firebase"; // ✅ Import Firebase Auth
import { getAuth, signOut } from "firebase/auth";

import OrdersTab from "../components/Account/OrdersTab";
import SubscriptionsTab from "../components/Account/SubscriptionsTab";
import AddressesTab from "../components/Account/AddressesTab";
import PaymentMethodsTab from "../components/Account/PaymentMethodsTab";
import SettingsTab from "../components/Account/SettingsTab";

import {
  getFirestore,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteField,
  collection,
  deleteDoc,
} from "firebase/firestore"; // ✅ Import Firestore functions
import valid from "card-validator";
import { setDoc } from "firebase/firestore"; // Add this at the top
import {
  fetchUserData,
  saveOrderToFirestore,
  saveSubscriptionToFirestore,
  updateUserAddress,
  deleteUserAddress,
} from "../firebaseUtils";
import AddPaymentForm from "../components/AddPaymentForm";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

const stripePromise = loadStripe("your_stripe_public_key");

const MyAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState({
    name: "",
    email: "",
    phone: "",
    addresses: [],
    paymentMethods: [],
    subscription: {},
    orders: [],
  });

  const [activeTab, setActiveTab] = useState("orders"); // ✅ Default tab
  const [showDropdown, setShowDropdown] = useState({
    orders: false,
    subscription: false,
    addresses: false,
    payment: false,
    settings: false,
  });

  const [showAddressForm, setShowAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    street: "",
    city: "",
    postalCode: "",
    country: "",
  });
  // State to hold subscriptions
  const [subscriptions, setSubscriptions] = useState([]);

  // ✅ State for editing address modal
  const [editingAddress, setEditingAddress] = useState(null); // Track the selected address for editing
  const [showEditModal, setShowEditModal] = useState(false); // Control visibility of the edit modal
  // ✅ State for Delete Confirmation Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false); // Controls modal visibility
  const [addressToDelete, setAddressToDelete] = useState(null); // Stores selected address

  // ✅ State for Success Message
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [newPayment, setNewPayment] = useState({
    type: "",
    last4: "",
    isDefault: false,
  });
  const [showSelectUpdateCard, setShowSelectUpdateCard] = useState(false);

  const toggleDropdown = (tab) => {
    setShowDropdown((prev) => ({
      ...prev,
      [tab]: !prev[tab],
    }));
  };

  const handleSetDefaultAddress = (id) => {
    setUser((prevUser) => ({
      ...prevUser,
      addresses: prevUser.addresses.map((addr) =>
        addr.id === id
          ? { ...addr, isDefault: true }
          : { ...addr, isDefault: false }
      ),
    }));
  };

  // ✅ Function to open edit modal
  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setShowEditModal(true);
  };

  // ✅ Function to save edited address
  const handleSaveEditedAddress = async () => {
    if (!editingAddress) return;

    const updatedAddresses = await updateUserAddress(editingAddress);
    setUser((prevUser) => ({ ...prevUser, addresses: updatedAddresses }));

    setShowEditModal(false);
    setEditingAddress(null);
  };

  // ✅ Open Delete Confirmation Modal
  const handleDeleteAddress = (addressId) => {
    setAddressToDelete(addressId); // Store the address being deleted
    setShowDeleteModal(true); // Show delete confirmation modal
  };

  // ✅ Confirm Deletion and Show Success Message
  const confirmDeleteAddress = async () => {
    if (!addressToDelete) return; // Ensure an address is selected

    const updatedAddresses = await deleteUserAddress(addressToDelete);
    setUser((prevUser) => ({ ...prevUser, addresses: updatedAddresses }));

    setShowDeleteModal(false); // Close the delete confirmation modal
    setShowSuccessMessage(true); // Show success message
  };

  const handleSetDefaultPayment = async (paymentId) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) return;

      let updatedPayments = userSnap.data().paymentMethods || {};
      Object.keys(updatedPayments).forEach((id) => {
        updatedPayments[id].isDefault = id === paymentId;
      });

      await updateDoc(userRef, { paymentMethods: updatedPayments });

      setUser((prevUser) => ({
        ...prevUser,
        paymentMethods: Object.entries(updatedPayments).map(([id, data]) => ({
          id,
          ...data,
        })),
      }));
    } catch (error) {
      console.error("Error setting default payment:", error);
    }
  };

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth); // Ensure Google sign-out
      localStorage.clear();
      sessionStorage.clear();

      setUser(null); // Reset user state
      navigate("/checkout"); // Redirect after logout

      window.location.reload(); // Ensure full logout
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // ✅ Fetch User Data & Payment Methods from Firestore
  const loadUserData = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();

        // Convert payment methods to an array
        const paymentsArray = userData.paymentMethods
          ? Object.entries(userData.paymentMethods).map(([id, data]) => ({
              id,
              ...data,
            }))
          : [];

        // ✅ Update state with user data
        setUser({
          name: userData.name || "",
          email: userData.email || "",
          phone: userData.phone || "",
          addresses: userData.addresses || [],
          paymentMethods: paymentsArray,
          orders: userData.orders || [],
          subscription: userData.subscription || null, // ✅ Ensure null if no subscription exists
        });
      } else {
        console.warn("⚠️ User document does not exist.");
      }
    } catch (error) {
      console.error("❌ Error fetching user data:", error);
    }
  };

  const handleAddAddress = async () => {
    try {
      console.log("📤 Adding Address...");

      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error("❌ No user logged in");
        return;
      }

      console.log("👤 Current User ID:", currentUser.uid);

      const userRef = doc(db, "users", currentUser.uid);
      console.log("🔍 Checking Firestore document path:", userRef.path);

      let userSnap = await getDoc(userRef);
      console.log(
        "📄 Firestore document fetched!",
        userSnap.exists() ? "✅ Exists" : "❌ Does not exist"
      );

      if (!userSnap.exists()) {
        console.log("🛠 User document does not exist. Creating a new one...");

        await setDoc(userRef, {
          name: currentUser.displayName || "",
          email: currentUser.email || "",
          phone: currentUser.phoneNumber || "",
          addresses: [],
          paymentMethods: [],
          subscription: {},
          orders: [],
        });

        console.log("✅ New user document created!");
        userSnap = await getDoc(userRef); // Fetch again after creating
      }

      let updatedAddresses = userSnap.data()?.addresses || [];

      if (
        !newAddress.street ||
        !newAddress.city ||
        !newAddress.postalCode ||
        !newAddress.country
      ) {
        console.error("❌ Address fields cannot be empty");
        return;
      }

      const addressId = `addr_${Date.now()}`;
      const newAddrData = { id: addressId, ...newAddress };

      updatedAddresses.push(newAddrData);

      console.log("📝 Updating Firestore with new address:", newAddrData);

      await updateDoc(userRef, { addresses: updatedAddresses });

      console.log("✅ Address added successfully!");

      setUser((prevUser) => ({ ...prevUser, addresses: updatedAddresses }));

      setNewAddress({ street: "", city: "", postalCode: "", country: "" });
      setShowAddressForm(false);
    } catch (error) {
      console.error("❌ Error adding address:", error);
    }
  };

  // ✅ Ensure it runs on page load
  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    setShowUpdateSelectModal(false);
    setShowAddPaymentModal(false); // Optional: close Add modal too if open
    setPaymentToUpdate(null);
  }, [activeTab]);

  //fetching users subscription
  useEffect(() => {
    const fetchSubscriptions = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        const subRef = collection(db, "users", user.uid, "subscriptions");
        const snapshot = await getDocs(subRef);
        const subs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSubscriptions(subs);
      } catch (err) {
        console.error("Error fetching subscriptions:", err);
      }
    };

    fetchSubscriptions();
  }, []);

  const handleRemovePayment = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !paymentToDelete) return;

      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
        [`paymentMethods.${paymentToDelete}`]: deleteField(),
      });

      const userSnap = await getDoc(userRef);
      const updated = userSnap.data().paymentMethods || {};

      setUser((prevUser) => ({
        ...prevUser,
        paymentMethods: Object.entries(updated).map(([id, data]) => ({
          id,
          ...data,
        })),
      }));

      setShowDeletePaymentModal(false);
      setShowDeletePaymentSuccess(true);
      setPaymentToDelete(null);
    } catch (error) {
      console.error("❌ Error removing payment method:", error);
    }
  };

  const handleCardInput = (event) => {
    if (!event || !event.complete) {
      console.warn("⚠️ Card details are incomplete!");
      return;
    }

    const { card } = event;
    if (!card || !card.brand) {
      console.error("❌ Card details not recognized:", event);
      return;
    }

    let detectedType = "";
    if (card.brand === "visa") detectedType = "Visa";
    else if (card.brand === "mastercard") detectedType = "MasterCard";
    else if (card.brand === "amex") detectedType = "American Express";
    else
      detectedType = card.brand.charAt(0).toUpperCase() + card.brand.slice(1); // Capitalize unknown brands

    // Ensure last4 exists
    const last4 = event.last4 ? event.last4 : "****";

    setNewPayment((prev) => ({
      ...prev,
      type: detectedType,
      last4: last4, // Store last 4 digits safely
    }));

    console.log("✅ Updated newPayment:", { type: detectedType, last4 });
  };

  const [cardType, setCardType] = useState("");

  const handleCardNumberChange = (value) => {
    const cardValidation = valid.number(value);
    setNewPayment({ ...newPayment, cardNumber: value });

    if (cardValidation.card) {
      setCardType(cardValidation.card.type); // Visa, MasterCard, etc.
    } else {
      setCardType(""); // Reset if invalid
    }
  };

  const handleSubscription = async () => {
    const newSubscription = {
      id: `sub_${Date.now()}`,
      plan: "Premium",
      renewalDate: "2025-04-15",
      active: true,
    };

    await saveSubscriptionToFirestore(newSubscription);
    loadUserData(); // Refresh UI with new subscription
  };

  const handleCancelSubscription = async () => {
    console.log("Cancel subscription clicked");
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      const subRef = collection(db, "users", currentUser.uid, "subscriptions");
      const snapshot = await getDocs(subRef);

      if (snapshot.empty) {
        console.warn("No subscriptions found for this user.");
        return;
      }

      // ✅ Find the latest active subscription (you can enhance this logic later if needed)
      const activeSubs = snapshot.docs.filter(
        (doc) => doc.data().active !== false
      );
      if (activeSubs.length === 0) {
        console.log("No active subscription to cancel.");
        return;
      }

      const latestSub = activeSubs[activeSubs.length - 1];

      // ✅ Update its `active` field to false
      await updateDoc(latestSub.ref, {
        active: false,
      });

      // ✅ Refresh the UI
      setShowCancelModal(false);
      setShowSuccessModal(true);

      // ✅ Re-fetch the updated subscriptions list
      const updatedSnapshot = await getDocs(subRef);
      const updatedSubs = updatedSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSubscriptions(updatedSubs);

      console.log("✅ Subscription successfully canceled.");
    } catch (error) {
      console.error("❌ Error cancelling subscription:", error);
      alert("An error occurred while cancelling your subscription.");
    }
  };

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleReactivateSubscription = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) return;

      const userData = userSnap.data();
      const paymentMethods = userData.paymentMethods || [];

      if (paymentMethods.length === 0) {
        alert(
          "No saved payment method. Please add a payment method before reactivating."
        );
        return;
      }

      const defaultPayment =
        paymentMethods.find((pm) => pm.isDefault) || paymentMethods[0];

      // ✅ Simulate charging the saved card (replace with real payment logic later)
      console.log("Charging card:", defaultPayment);
      await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulate processing time

      // ✅ Update subscription status in Firestore
      await updateDoc(userRef, {
        "subscription.active": true,
      });

      loadUserData(); // ✅ Refresh UI
      alert("Subscription reactivated successfully!");
    } catch (error) {
      console.error("❌ Error reactivating subscription:", error);
      alert("An error occurred while reactivating your subscription.");
    }
  };

  const handleConfirmDeleteSubscription = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !subscriptionToDelete) return;

      const subRef = doc(
        db,
        "users",
        currentUser.uid,
        "subscriptions",
        subscriptionToDelete
      );
      await deleteDoc(subRef);

      setSubscriptions((prev) =>
        prev.filter((sub) => sub.id !== subscriptionToDelete)
      );

      setShowDeleteSubscriptionModal(false);
      setSubscriptionToDelete(null);
    } catch (err) {
      console.error("❌ Failed to delete subscription:", err);
    }
  };

  const [showDeletePaymentModal, setShowDeletePaymentModal] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [showDeletePaymentSuccess, setShowDeletePaymentSuccess] =
    useState(false);

  const [paymentToUpdate, setPaymentToUpdate] = useState(null);
  const [showUpdateSelectModal, setShowUpdateSelectModal] = useState(false);

  const [showDeleteSubscriptionModal, setShowDeleteSubscriptionModal] =
    useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);

  //orders

  const [userOrders, setUserOrders] = useState([]);

  useEffect(() => {
    const fetchOrders = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        const ordersRef = collection(db, "users", user.uid, "orders");
        const snapshot = await getDocs(ordersRef);

        const orders = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setUserOrders(orders);
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      }
    };

    fetchOrders();
  }, []);

  const [showDeleteOrderModal, setShowDeleteOrderModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);

  const confirmDeleteOrder = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !orderToDelete) return;

      const orderRef = doc(
        db,
        "users",
        currentUser.uid,
        "orders",
        orderToDelete
      );
      await deleteDoc(orderRef);

      setUserOrders((prev) => prev.filter((o) => o.id !== orderToDelete));
      setOrderToDelete(null);
      setShowDeleteOrderModal(false);
    } catch (err) {
      console.error("Failed to delete order:", err);
    }
  };

  //orders logic
  const handleCancelOrder = async (orderId) => {
    // Simulate cancellation — update Firestore field "status" to "cancelled"
    const user = auth.currentUser;
    if (!user) return;

    const orderRef = doc(db, "users", user.uid, "orders", orderId);
    await updateDoc(orderRef, { status: "cancelled" });

    // Refresh orders
    const updatedOrders = userOrders.map((o) =>
      o.id === orderId ? { ...o, status: "cancelled" } : o
    );
    setUserOrders(updatedOrders);
  };

  const handleReactivateOrder = async (orderId) => {
    const user = auth.currentUser;
    if (!user) return;

    const orderRef = doc(db, "users", user.uid, "orders", orderId);
    await updateDoc(orderRef, { status: "active" });

    const updatedOrders = userOrders.map((o) =>
      o.id === orderId ? { ...o, status: "active" } : o
    );
    setUserOrders(updatedOrders);
  };

  return (
    <div className="page-wrapper">
      <div className="account-container">
        <div className="page-content">
          {" "}
          {/* ✅ New Wrapper for Proper Page Layout */}
          <h1>{t("account.title")}</h1>
          {/* ✅ Tabs Navigation */}
          <div className="tabs">
            <button
              className={activeTab === "orders" ? "active" : ""}
              onClick={() => setActiveTab("orders")}
            >
              {t("account.orders")}
            </button>
            <button
              className={activeTab === "subscription" ? "active" : ""}
              onClick={() => setActiveTab("subscription")}
            >
              {t("account.subscription")}
            </button>
            <button
              className={activeTab === "addresses" ? "active" : ""}
              onClick={() => setActiveTab("addresses")}
            >
              {t("account.addresses")}
            </button>
            <button
              className={activeTab === "payment" ? "active" : ""}
              onClick={() => setActiveTab("payment")}
            >
              {t("account.payment_methods")}
            </button>
            <button
              className={activeTab === "settings" ? "active" : ""}
              onClick={() => setActiveTab("settings")}
            >
              {t("account.settings")}
            </button>
          </div>
          {activeTab === "orders" && (
            <OrdersTab
              userOrders={userOrders}
              t={t}
              showDropdown={showDropdown.orders}
              toggleDropdown={toggleDropdown}
              handleCancelOrder={handleCancelOrder}
              handleReactivateOrder={handleReactivateOrder}
              navigate={navigate}
              setOrderToDelete={setOrderToDelete}
              setShowDeleteOrderModal={setShowDeleteOrderModal}
              showDeleteOrderModal={showDeleteOrderModal}
              confirmDeleteOrder={confirmDeleteOrder}
            />
          )}
          {activeTab === "subscription" && (
            <SubscriptionsTab
              subscriptions={subscriptions}
              t={t}
              navigate={navigate}
              showDropdown={showDropdown.subscription}
              toggleDropdown={toggleDropdown}
              handleReactivateSubscription={handleReactivateSubscription}
              setShowCancelModal={setShowCancelModal}
              setShowSuccessModal={setShowSuccessModal}
              showCancelModal={showCancelModal}
              setShowDeleteSubscriptionModal={setShowDeleteSubscriptionModal}
              setSubscriptionToDelete={setSubscriptionToDelete}
              handleCancelSubscription={handleCancelSubscription}
              handleConfirmDeleteSubscription={handleConfirmDeleteSubscription}
              showDeleteSubscriptionModal={showDeleteSubscriptionModal}
              subscriptionToDelete={subscriptionToDelete}
            />
          )}
          {activeTab === "addresses" && (
            <AddressesTab
              user={user}
              t={t}
              showDropdown={showDropdown.addresses}
              toggleDropdown={toggleDropdown}
              showAddressForm={showAddressForm}
              setShowAddressForm={setShowAddressForm}
              newAddress={newAddress}
              setNewAddress={setNewAddress}
              handleAddAddress={handleAddAddress}
              handleEditAddress={handleEditAddress}
              editingAddress={editingAddress}
              setEditingAddress={setEditingAddress}
              handleSaveEditedAddress={handleSaveEditedAddress}
              handleDeleteAddress={handleDeleteAddress}
              showEditModal={showEditModal}
              setShowEditModal={setShowEditModal}
              showDeleteModal={showDeleteModal}
              setShowDeleteModal={setShowDeleteModal}
              confirmDeleteAddress={confirmDeleteAddress}
              showSuccessMessage={showSuccessMessage}
              setShowSuccessMessage={setShowSuccessMessage}
            />
          )}
          {activeTab === "payment" && (
            <PaymentMethodsTab
              user={user}
              t={t}
              showDropdown={showDropdown.payment}
              toggleDropdown={toggleDropdown}
              handleSetDefaultPayment={handleSetDefaultPayment}
              showAddPaymentModal={showAddPaymentModal}
              setShowAddPaymentModal={setShowAddPaymentModal}
              paymentToUpdate={paymentToUpdate}
              setPaymentToUpdate={setPaymentToUpdate}
              newPayment={newPayment}
              setNewPayment={setNewPayment}
              showSelectUpdateCard={showSelectUpdateCard}
              setShowSelectUpdateCard={setShowSelectUpdateCard}
              showDeletePaymentModal={showDeletePaymentModal}
              setShowDeletePaymentModal={setShowDeletePaymentModal}
              showDeletePaymentSuccess={showDeletePaymentSuccess}
              setShowDeletePaymentSuccess={setShowDeletePaymentSuccess}
              paymentToDelete={paymentToDelete}
              setPaymentToDelete={setPaymentToDelete}
              handleRemovePayment={handleRemovePayment}
              stripePromise={stripePromise}
              AddPaymentForm={AddPaymentForm}
              Elements={Elements}
              setUser={setUser}
            />
          )}
          {activeTab === "settings" && (
            <SettingsTab t={t} handleLogout={handleLogout} />
          )}
        </div>
        
      </div>
      <Footer />
    </div>
  );
};

export default MyAccount;
